import { styled } from '@linaria/react'
import React, { type FC } from 'react'
import { FormattedMessage } from 'react-intl'
import { colors } from '../styles/colors'
import type { VoteSubmitDto } from '../types/generated'
import { OptionButton } from '../ui/OptionButton'
import { Typography } from '../ui/Typography'
import { formatTon } from '../utils/formatTon'
import { decodeHtmlEntities } from '../utils/decodeHtmlEntities'
import SvgVbx from '../svgr/Vbx'
import { ProcessingVotesList } from './ProcessingVotesList'

export const VoteOptionWithMeta: FC<{
  option: {
    percent: number
    userVotes?: number
    userTon?: number
    choice: string
    voteCount: string
    choiceIndex: number
    votesSubmitted?: VoteSubmitDto[]
  }
  checked: boolean
  disabled: boolean
  onChange: (choiceIndex: number) => void
  profitabilityPercent: number
  isRightAnswer?: boolean | null
  isProfitabilityShow?: boolean
}> = ({
  checked,
  disabled,
  isProfitabilityShow,
  isRightAnswer,
  onChange,
  option,
  profitabilityPercent,
}) => {
  return (
    <OptionButton
      value={option.choiceIndex}
      onChange={onChange}
      disabled={disabled}
      checked={checked}
      isRightAnswer={isRightAnswer}
    >
      <OptionContent>
        <TopSection>
          <Typography
            fontSize={13}
            fontWeight={700}
            font="manrope"
            color={colors.textPrimary}
          >
            {decodeHtmlEntities(option.choice)}
          </Typography>

          <MetricsContainer>
            <PercentContainer checked={checked}>
              <Typography
                fontSize={checked ? 14 : 12}
                fontWeight={600}
                font="roboto"
              >
                {option.percent}%
              </Typography>
            </PercentContainer>

            <Separator />

            <TonContainer>
              <Typography
                fontSize={14}
                fontWeight={400}
                font="roboto"
                color={colors.textPrimary}
              >
                {formatTon(option.voteCount)}
              </Typography>

              <SvgVbx />
            </TonContainer>
          </MetricsContainer>
        </TopSection>

        {isProfitabilityShow && profitabilityPercent > 0 && (
          <ProfitabilitySection>
            <Typography
              fontSize={12}
              fontWeight={400}
              style={{ color: colors.grey700 }}
            >
              <FormattedMessage
                id="amountInput.profitability"
                defaultMessage="Доходность:"
              />
            </Typography>

            <Typography
              fontSize={12}
              fontWeight={600}
              font="roboto"
              color={colors.textPrimary}
            >
              {profitabilityPercent.toFixed(2)}%
            </Typography>
          </ProfitabilitySection>
        )}

        {Boolean(option.userVotes) && Boolean(option.userTon) && (
          <UserVotesSection>
            <Typography
              fontSize={10}
              fontWeight={900}
              style={{ color: colors.grey600 }}
            >
              <FormattedMessage
                id="youVoted"
                defaultMessage="Вы проголосовали:"
              />{' '}
            </Typography>

            <Typography
              fontSize={10}
              fontWeight={700}
              color={colors.textPrimary}
            >
              <FormattedMessage
                id="votes.count"
                defaultMessage="{count, plural, one {# голос} few {# голоса} many {# голосов} other {# голосов}}"
                values={{ count: formatTon(String(option.userVotes)) }}
              />
            </Typography>

            <Typography
              fontSize={10}
              fontWeight={700}
              style={{ color: colors.textSecondary }}
            >
              {' / '}
            </Typography>

            <Typography
              fontSize={10}
              fontWeight={700}
              font="roboto"
              color={colors.textPrimary}
            >
              {formatTon(String(option.userTon ?? ''))} TON
            </Typography>
          </UserVotesSection>
        )}

        <ProcessingVotesList votesSubmitted={option.votesSubmitted ?? []} />
      </OptionContent>
    </OptionButton>
  )
}

const OptionContent = styled.div`
  display: grid;
  grid-gap: 4px;
  gap: 4px;
`

const TopSection = styled.div`
  display: grid;
  grid-auto-flow: column;
  grid-gap: 4px;
  gap: 4px;
  justify-content: space-between;
  align-items: center;
  width: 100%;
`

const MetricsContainer = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 8px;
  align-items: center;
`

const UserVotesSection = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
`

const PercentContainer = styled.div<{ checked: boolean }>`
  color: ${props => (props.checked ? colors.textSecondary : colors.textMuted)};
  text-align: right;
  flex-shrink: 0;
`

const Separator = styled.div`
  width: 1px;
  height: 20px;
  background: ${colors.grey200};
`

const TonContainer = styled.div`
  display: grid;
  gap: 4px;
  grid-gap: 4px;
  grid-template-columns: 1fr auto;
  min-width: 38px;
  justify-items: flex-end;
  align-items: center;
`

const ProfitabilitySection = styled.div`
  display: grid;
  grid-auto-flow: column;
  align-items: center;
  justify-content: flex-start;
  gap: 2px;
`
